// ─────────────────────────────────────────────────────────────────────────────
// Moteur de synchronisation clipboard partagé (RDP + Vidéo).
//
// Ce module est AGNOSTIQUE DU TRANSPORT : il gère l'état (mode, historique),
// la lecture du presse-papier navigateur, et la décision "paste intelligent +
// fallback". Chaque panneau implémente un `ClipboardTransport` qui sait comment
// pousser/récupérer le clipboard côté distant (CLIPRDR pour RDP, messages WS
// pour la Vidéo).
//
// Le moteur NE connaît ni IronRDP, ni la WebSocket, ni l'agent C# : il ne fait
// que coordonner. Cela permet de tester toute la logique de sync sans WASM ni
// réseau, et de réutiliser le même comportement sur les deux panneaux.
// ─────────────────────────────────────────────────────────────────────────────

import { createSignal } from "solid-js";
import type { Accessor } from "solid-js";

// ── Types partagés ───────────────────────────────────────────────────────────

export type ClipboardMode = "none" | "local-to-remote" | "remote-to-local" | "bidirectional";

export type ClipboardFormat = "text/plain" | "text/html" | "image/png" | "files";

/**
 * Instantané du presse-papier. Format neutre : le transport se charge de
 * convertir vers son propre format (CLIPRDR `ClipboardData`, message WS
 * `clipboard:set`, etc.). Les champs absents = format absent.
 */
export interface ClipboardSnapshot {
  text?: string;
  html?: string;
  png?: Uint8Array;
  /** Fichiers (panel Vidéo via le proto `file:*`). `hostPath` = chemin côté host. */
  files?: { hostPath: string; name: string; size: number }[];
}

/** Entrée d'historique. On ne stocke JAMAIS le contenu des gros fichiers. */
export interface ClipboardEntry {
  id: string;
  origin: "local" | "remote";
  formats: ClipboardFormat[];
  /** Aperçu texte (200 premiers caractères) pour l'UI. */
  textPreview: string;
  /** Texte complet, seulement si ≤ `CLIPBOARD_HISTORY_MAX_TEXT_BYTES`. */
  text?: string;
  /** Métadonnées fichiers uniquement (nom + taille), jamais le contenu. */
  fileRefs?: { name: string; size: number }[];
  timestamp: number;
}

/**
 * Contrat que chaque panneau implémente pour le transport clipboard.
 * Le moteur appelle ces méthodes ; le panneau traduit vers son backend.
 */
export interface ClipboardTransport {
  /**
   * Pousse un snapshot vers le presse-papier distant.
   * @returns `true` si le transport a acquitté la réception (ex. handshake
   *   CLIPRDR terminé, message WS envoyé). `false` si échec ou non supporté.
   */
  pushToRemote(snapshot: ClipboardSnapshot): Promise<boolean>;
  /**
   * Demande le presse-papier distant. Peut être asynchrone (WS) ou résolu via
   * un callback différé (`onRemoteChanged`). Retourne `null` si indispo.
   */
  requestFromRemote(): Promise<ClipboardSnapshot | null>;
  /** Le distant supporte-t-il le rich-text (HTML/images) ? RDP oui (CLIPRDR), Vidéo non. */
  supportsRichText(): boolean;
  /** Le distant supporte-t-il les fichiers ? Vidéo oui (`file:*`), RDP non. */
  supportsFiles(): boolean;
}

export type PasteResult =
  | { kind: "synced" } // Snapshot poussé + succès transport.
  | { kind: "native-fallback"; reason: string } // Sync impossible → l'appelant envoie le collage natif.
  | { kind: "noop"; reason: "mode-disabled" | "empty" | "in-flight" };

// ── Constantes ───────────────────────────────────────────────────────────────

/** Nombre max d'entrées dans l'historique (LRU). */
export const CLIPBOARD_HISTORY_MAX_ENTRIES = 20;
/** On ne stocke pas le texte complet au-delà de cette taille (aperçu seulement). */
export const CLIPBOARD_HISTORY_MAX_TEXT_BYTES = 256 * 1024;
/** Longueur de l'aperçu texte stocké dans l'historique. */
export const CLIPBOARD_HISTORY_PREVIEW_CHARS = 200;

/** Clé localStorage pour la persistance du mode (global, pas par session). */
const CLIPBOARD_MODE_STORAGE_KEY = "rws.clipboard.mode";

// ── Helpers purs (testables sans DOM ni SolidJS) ─────────────────────────────

export function clipboardAllowsLocalToRemote(mode: ClipboardMode): boolean {
  return mode === "local-to-remote" || mode === "bidirectional";
}

export function clipboardAllowsRemoteToLocal(mode: ClipboardMode): boolean {
  return mode === "remote-to-local" || mode === "bidirectional";
}

export function defaultClipboardMode(): ClipboardMode {
  // `bidirectional` : la plupart des opérateurs attendent un presse-papier qui
  // marche sans configuration. Le mode reste modifiable en live (le moteur lit
  // `mode()` au moment de chaque opération).
  return "bidirectional";
}

/** Génère un aperçu texte tronqué pour l'historique. */
export function textPreviewOf(text: string | undefined): string {
  if (!text) return "";
  const cleaned = text.replace(/\s+/g, " ").trim();
  return cleaned.length > CLIPBOARD_HISTORY_PREVIEW_CHARS
    ? `${cleaned.slice(0, CLIPBOARD_HISTORY_PREVIEW_CHARS)}…`
    : cleaned;
}

/** Construit la liste des formats présents dans un snapshot. */
export function formatsOf(snapshot: ClipboardSnapshot): ClipboardFormat[] {
  const fmts: ClipboardFormat[] = [];
  if (snapshot.text != null && snapshot.text !== "") fmts.push("text/plain");
  if (snapshot.html != null && snapshot.html !== "") fmts.push("text/html");
  if (snapshot.png != null && snapshot.png.byteLength > 0) fmts.push("image/png");
  if (snapshot.files && snapshot.files.length > 0) fmts.push("files");
  return fmts;
}

/** `true` si le snapshot est vide (aucun format). */
export function isSnapshotEmpty(snapshot: ClipboardSnapshot): boolean {
  return formatsOf(snapshot).length === 0;
}

/**
 * Crée une entrée d'historique à partir d'un snapshot.
 *
 * Règle de stockage (cf. `CLIPBOARD_HISTORY_MAX_TEXT_BYTES`) :
 *  - texte ≤ 256 Ko : on garde le texte complet (utile pour re-pousser) ;
 *  - texte > 256 Ko : aperçu seulement (on ne gonfle pas le localStorage/la RAM) ;
 *  - fichiers : métadonnées uniquement (nom + taille), JAMAIS le contenu.
 */
export function entryFromSnapshot(
  snapshot: ClipboardSnapshot,
  origin: "local" | "remote",
  timestamp: number,
  id: string,
): ClipboardEntry {
  const formats = formatsOf(snapshot);
  const text = snapshot.text;
  const storeFullText = typeof text === "string" && text.length <= CLIPBOARD_HISTORY_MAX_TEXT_BYTES;
  return {
    id,
    origin,
    formats,
    textPreview: textPreviewOf(text),
    text: storeFullText ? text : undefined,
    fileRefs: snapshot.files?.map((f) => ({ name: f.name, size: f.size })),
    timestamp,
  };
}

/**
 * Décide si une nouvelle entrée doit remplacer la dernière (même contenu).
 * Évite les doublons consécutifs dans l'historique (même texte = même entrée).
 */
export function shouldDeduplicate(entry: ClipboardEntry, lastEntry: ClipboardEntry | undefined): boolean {
  if (!lastEntry) return false;
  if (entry.origin !== lastEntry.origin) return false;
  return entry.textPreview === lastEntry.textPreview && entry.formats.join(",") === lastEntry.formats.join(",");
}

// ── Persistance du mode ──────────────────────────────────────────────────────

export function loadClipboardMode(): ClipboardMode {
  try {
    const v = localStorage.getItem(CLIPBOARD_MODE_STORAGE_KEY);
    if (v === "none" || v === "local-to-remote" || v === "remote-to-local" || v === "bidirectional") return v;
  } catch {
    /* localStorage indisponible */
  }
  return defaultClipboardMode();
}

export function persistClipboardMode(mode: ClipboardMode): void {
  try {
    localStorage.setItem(CLIPBOARD_MODE_STORAGE_KEY, mode);
  } catch {
    /* localStorage indisponible — réglage non persisté */
  }
}

// ── Lecture du presse-papier navigateur (DI pour testabilité) ─────────────────

/**
 * Contrat d'accès au presse-papier navigateur. En production on injecte
 * l'implémentation réelle (navigator.clipboard) ; en test on injecte un mock.
 * Cela évite de mocker `navigator` global dans les tests unitaires.
 */
export interface BrowserClipboardReader {
  /** Lit le presse-papier riche (text/html, text/plain, image/png) si l'API le permet. */
  readRich(): Promise<ClipboardSnapshot | null>;
  /** Lit le presse-papier texte seul (fallback si `readRich` indispo/refusé). */
  readText(): Promise<string | null>;
}

/**
 * Implémentation réelle de `BrowserClipboardReader`, basée sur l'API Clipboard
 * du navigateur. Gère les permission denials gracieusement (retourne `null`).
 */
export function createBrowserClipboardReader(transport: ClipboardTransport): BrowserClipboardReader {
  return {
    async readRich(): Promise<ClipboardSnapshot | null> {
      const clip = typeof navigator !== "undefined" ? (navigator.clipboard as Clipboard | undefined) : undefined;
      if (!clip) return null;
      if (!transport.supportsRichText()) return null; // Vidéo : texte only, pas la peine de lire le riche.
      const snap: ClipboardSnapshot = {};
      const read = clip as Clipboard & {
        read?: () => Promise<Array<{ types: readonly string[]; getType: (type: string) => Promise<Blob> }>>;
      };
      if (typeof read.read === "function") {
        try {
          const items = await read.read();
          for (const item of items) {
            if (item.types.includes("text/html")) {
              const html = await (await item.getType("text/html")).text();
              if (html) snap.html = html;
            }
            if (item.types.includes("text/plain")) {
              const text = await (await item.getType("text/plain")).text();
              if (text) snap.text = text;
            }
            if (item.types.includes("image/png")) {
              const buf = await (await item.getType("image/png")).arrayBuffer();
              snap.png = new Uint8Array(buf);
            }
          }
        } catch {
          /* permission refusée ou API indisponible → fallback readText */
        }
      }
      if (snap.text == null && typeof clip.readText === "function") {
        try {
          const text = await clip.readText();
          if (text) snap.text = text;
        } catch {
          /* permission refusée */
        }
      }
      return isSnapshotEmpty(snap) ? null : snap;
    },
    async readText(): Promise<string | null> {
      const clip = typeof navigator !== "undefined" ? (navigator.clipboard as Clipboard | undefined) : undefined;
      if (!clip || typeof clip.readText !== "function") return null;
      try {
        const text = await clip.readText();
        return text || null;
      } catch {
        return null; // permission refusée.
      }
    },
  };
}

// ── Moteur ───────────────────────────────────────────────────────────────────

export interface ClipboardEngineOptions {
  /** Lecteur de presse-papier navigateur (DI). Défaut : `createBrowserClipboardReader`. */
  reader?: BrowserClipboardReader;
  /** Générateur d'IDs pour l'historique. Défaut : `crypto.randomUUID`. */
  generateId?: () => string;
}

export interface ClipboardEngine {
  mode: Accessor<ClipboardMode>;
  setMode: (m: ClipboardMode) => void;
  history: Accessor<ClipboardEntry[]>;
  pushHistory: (entry: ClipboardEntry) => void;
  clearHistory: () => void;
  /** True si un paste est en cours (anti-réentrance). */
  pasteInFlight: Accessor<boolean>;

  /**
   * Pousse le presse-papier LOCAL vers le distant (sens local→remote).
   *
   * Logique "paste intelligent + fallback" :
   *  1. Lit le clipboard navigateur (riche si supporté, sinon texte).
   *  2. Si vide → `noop` (rien à coller, l'appelant ne devrait pas coller non plus).
   *  3. Pousse via le transport. Si succès → `synced`.
   *  4. Si échec transport → `native-fallback` : l'appelant doit envoyer le collage
   *     natif (Ctrl+V scancode pour RDP, keyText pour Vidéo) pour coller le
   *     presse-papier du DISTANT (pas le local).
   *
   * Le `native-fallback` est crucial pour le Ctrl+V RDP : si la sync CLIPRDR
   * échoue (permission, handshake mort), on envoie quand même le scancode Ctrl+V
   * au Windows distant qui collera son PROPRE presse-papier. Sans ça, l'opérateur
   * voit "Ctrl+V ne fait rien".
   */
  pasteLocalIntoRemote(): Promise<PasteResult>;

  /**
   * Demande le presse-papier DISTANT vers le local (sens remote→local).
   * Le transport peut résoudre via `requestFromRemote()` ou via un callback
   * différé (`onRemoteChanged`).
   */
  requestRemoteIntoLocal(): Promise<void>;

  /**
   * Callback invoqué par le panneau quand le distant notifie un changement de
   * presse-papier (ex. `remoteClipboardChangedCallback` IronRDP, ou message WS
   * `clipboard:sync`). Le moteur écrit dans le clipboard navigateur (si le mode
   * autorise remote→local) et pousse dans l'historique.
   *
   * `writeLocal` = fonction d'écriture du clipboard navigateur (DI pour test).
   */
  onRemoteChanged(snapshot: ClipboardSnapshot, writeLocal?: (snapshot: ClipboardSnapshot) => Promise<void>): void;
}

/**
 * Crée un moteur clipboard lié à un transport.
 *
 * Le moteur garde un état (mode, historique, paste-in-flight) via des signals
 * SolidJS. Le transport est injecté et reste constant pour la vie du moteur.
 */
export function createClipboardEngine(
  transport: ClipboardTransport,
  opts: ClipboardEngineOptions = {},
): ClipboardEngine {
  const reader = opts.reader ?? createBrowserClipboardReader(transport);
  const generateId =
    opts.generateId ??
    (() =>
      typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random()}`);

  const [mode, setModeSignal] = createSignal<ClipboardMode>(loadClipboardMode());
  const [history, setHistory] = createSignal<ClipboardEntry[]>([]);
  const [pasteInFlight, setPasteInFlight] = createSignal(false);

  function setMode(m: ClipboardMode): void {
    setModeSignal(m);
    persistClipboardMode(m);
  }

  function pushHistory(entry: ClipboardEntry): void {
    setHistory((prev) => {
      // Déduplication : si la dernière entrée a le même contenu, on ne rajoute pas.
      if (shouldDeduplicate(entry, prev[0])) {
        return [{ ...entry, id: prev[0].id, timestamp: entry.timestamp }, ...prev.slice(1)];
      }
      const next = [entry, ...prev];
      if (next.length > CLIPBOARD_HISTORY_MAX_ENTRIES) next.length = CLIPBOARD_HISTORY_MAX_ENTRIES;
      return next;
    });
  }

  function clearHistory(): void {
    setHistory([]);
  }

  async function readLocalSnapshot(): Promise<ClipboardSnapshot | null> {
    // Riche d'abord (HTML/PNG), fallback texte si vide ou non supporté.
    const rich = await reader.readRich();
    if (rich && !isSnapshotEmpty(rich)) return rich;
    const text = await reader.readText();
    if (text) return { text };
    return null;
  }

  async function pasteLocalIntoRemote(): Promise<PasteResult> {
    if (!clipboardAllowsLocalToRemote(mode())) return { kind: "noop", reason: "mode-disabled" };
    if (pasteInFlight()) return { kind: "noop", reason: "in-flight" };
    setPasteInFlight(true);
    try {
      const snapshot = await readLocalSnapshot();
      if (!snapshot || isSnapshotEmpty(snapshot)) return { kind: "noop", reason: "empty" };
      pushHistory(entryFromSnapshot(snapshot, "local", Date.now(), generateId()));
      const ok = await transport.pushToRemote(snapshot);
      if (!ok) return { kind: "native-fallback", reason: "transport-rejected" };
      return { kind: "synced" };
    } catch (err) {
      // Erreur (permission refusée, lecture impossible) → fallback natif.
      console.warn("[clipboard] paste local→remote échoué:", err);
      return { kind: "native-fallback", reason: String(err) };
    } finally {
      setPasteInFlight(false);
    }
  }

  async function requestRemoteIntoLocal(): Promise<void> {
    if (!clipboardAllowsRemoteToLocal(mode())) return;
    try {
      // Le transport peut résoudre directement (WS synchrone) ou différé
      // (callback `onRemoteChanged` pour CLIPRDR). On déclenche la demande ;
      // l'arrivée du contenu sera traitée par `onRemoteChanged`.
      await transport.requestFromRemote();
    } catch (err) {
      console.warn("[clipboard] request remote→local échoué:", err);
    }
  }

  function onRemoteChanged(snapshot: ClipboardSnapshot, writeLocal?: (s: ClipboardSnapshot) => Promise<void>): void {
    if (!clipboardAllowsRemoteToLocal(mode())) return;
    if (isSnapshotEmpty(snapshot)) return;
    pushHistory(entryFromSnapshot(snapshot, "remote", Date.now(), generateId()));
    if (writeLocal) void writeLocal(snapshot);
  }

  return {
    mode,
    setMode,
    history,
    pushHistory,
    clearHistory,
    pasteInFlight,
    pasteLocalIntoRemote,
    requestRemoteIntoLocal,
    onRemoteChanged,
  };
}
